import React, {useEffect, useState} from 'react'
import {Button, CircularProgress, Stack} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import ReorderIcon from "@mui/icons-material/Reorder"
import {AnyColumnJson} from "../../api/json/ColumnJson"
import {ProjectJson} from "../../api/json/ProjectJson"
import {useProjectData} from "../../hooks/useProjectData"
import {ColumnRequests} from "../../api/requests/ColumnRequests"
import {RoleCheck} from "../../library/RoleCheck"
import ApiError from "../../api/ApiError"
import ErrorMessage from "../error/ErrorMessage"
import ColumnsTable from "../columns/ColumnsTable"
import ColumnModal from "../columns/ColumnModal"
import ColumnOrderModal from "../columns/ColumnOrderModal"

function ColumnsTab({project}: { project: ProjectJson }) {
    const {id, reloadProject} = useProjectData()
    const [columns, setFields] = useState<AnyColumnJson[]>(project.columns ?? [])
    const [error, setError] = useState<ApiError>()

    const [modalOpen, setModalOpen] = useState(false)
    const [editingColumn, setEditingColumn] = useState<AnyColumnJson>()

    const [orderModalOpen, setOrderModalOpen] = useState(false)

    const loadFields = () => {
        ColumnRequests.list(id)
            .then(setFields)
            .catch(setError)
    }

    useEffect(() => loadFields(), [])

    const handleEdit = (column: AnyColumnJson) => {
        setEditingColumn(column)
        setModalOpen(true)
        reloadProject()
        loadFields()
    }

    const handleClose = () => {
        setEditingColumn(undefined)
        setModalOpen(false)
        reloadProject()
        loadFields()
    }

    if (!columns) {
        return <CircularProgress/>
    }

    if (error) {
        return <ErrorMessage error={error}/>
    }

    const isAdmin = RoleCheck.isAdmin()

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{alignSelf: 'flex-start'}}>
                {isAdmin &&
                    <Button variant="contained"
                            startIcon={<AddIcon/>}
                            onClick={() => {
                                setEditingColumn(undefined)
                                setModalOpen(true)
                            }}>
                        Add Column
                    </Button>
                }
                {isAdmin && columns.length > 1 &&
                    <Button variant="outlined"
                            color="secondary"
                            startIcon={<ReorderIcon/>}
                            onClick={() => setOrderModalOpen(true)}>
                        Change Order
                    </Button>
                }
            </Stack>

            {columns.length > 0 &&
                <ColumnsTable project={project} columns={columns} handleEdit={handleEdit}/>
            }

            <ColumnModal project={project}
                        column={editingColumn}
                        open={modalOpen}
                        close={() => handleClose()}/>

            <ColumnOrderModal project={project}
                             columns={columns}
                             open={orderModalOpen}
                             onClose={() => {
                                 setOrderModalOpen(false)
                                 reloadProject()
                                 loadFields()
                             }}/>
        </Stack>
    )
}

export default ColumnsTab
